/**
 * /app/settings — my account.
 *
 * Per-account user-facing settings. Master picks which of their
 * accounts (themselves or a sub) they're editing, and the cards
 * below act on that account.
 *
 *   Account picker (master only)  → top of page
 *
 *   1. Account            — id / email / role / label for picked account
 *   2. API keys           — Bybit creds + demo/real toggle (per-account)
 *   3. Risk limits        — max_concurrent + daily_loss_pct (per-account)
 *   4. Telegram           — chat_id + flags + test button (per-account)
 *   5. Sub-accounts       — master-only table (rename / activate)
 *   6. Active risk blocks — feed + master-only "clear"
 *
 * Trading parameters (DCA / trailing / regime / etc.) are GLOBAL and
 * live exclusively in /app/admin/settings. They're not per-account.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import {
  type MeSettings,
  type MeSubAccount,
  type UserRiskBlock,
  clearMyRiskBlock,
  createSubAccount,
  patchMeSettings,
  patchSubAccount,
  putApiKeys,
  testTelegram,
  toggleTestnet,
  useMeSettings,
  useMyRiskBlocks,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { timeAgo } from '@/lib/format'

export default function MySettings() {
  const { t } = useTranslation('app')

  // Caller's own profile — also gives us the list of sub-accounts to
  // populate the picker. The same query runs again when the picker
  // picks a different target (different cache key).
  const { data: self } = useMeSettings()

  // `null` (or self.id) = caller is editing their own row. A number =
  // caller is editing a sub-account they own. Default to self.
  const [accountId, setAccountId] = useState<number | null>(null)
  const effectiveId = accountId ?? self?.id ?? null

  const { data, isLoading } = useMeSettings(effectiveId)

  return (
    <div className="p-4 sm:p-6 max-w-[900px]">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('my_settings.title')}
          </h1>
          <p className="text-sm text-textDim mt-1">
            {t('my_settings.subtitle')}
          </p>
        </div>
        {self?.is_master && self.sub_accounts.length > 0 && (
          <AccountPicker
            self={self}
            value={accountId ?? self.id}
            onChange={(v) => setAccountId(v === self.id ? null : v)}
          />
        )}
      </header>

      {isLoading || !data ? (
        <div className="bg-ink border border-line px-5 py-10 text-center text-textFaint text-sm">—</div>
      ) : (
        <div className="space-y-4">
          <AccountCard data={data} accountId={accountId} />
          <ApiKeysCard data={data} accountId={accountId} />
          <RiskCard data={data} accountId={accountId} />
          <TelegramCard data={data} accountId={accountId} />
          {/* Sub-accounts list only makes sense on the master view */}
          {self?.is_master && (accountId == null) && self.sub_accounts.length > 0 && (
            <SubAccountsCard data={self} />
          )}
          <RiskBlocksCard isMaster={data.is_master} />
        </div>
      )}
    </div>
  )
}

/* ─── Account picker ─────────────────────────────────────────── */

function AccountPicker({
  self, value, onChange,
}: {
  self: MeSettings
  value: number
  onChange: (id: number) => void
}) {
  const { t } = useTranslation('app')
  return (
    <div className="flex items-center gap-2 text-xs">
      <label className="text-textDim uppercase tracking-wider">
        {t('my_settings.picker.label')}
      </label>
      <Select
        value={String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-64"
      >
        <option value={String(self.id)}>
          {t('my_settings.picker.self', {
            label: self.account_label || self.email || `#${self.id}`,
          })}
        </option>
        {self.sub_accounts.map((s) => (
          <option key={s.id} value={String(s.id)}>
            #{s.id} {s.account_label ?? ''} · {s.bybit_testnet ? 'demo' : 'real'}
          </option>
        ))}
      </Select>
    </div>
  )
}

/* ─── Section primitive ─────────────────────────────────────────── */

function Card({
  title, hint, children, footer,
}: {
  title: string
  hint?: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <section className="bg-ink border border-line">
      <header className="px-5 py-3 border-b border-line">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {hint && <p className="text-xs text-textDim mt-1">{hint}</p>}
      </header>
      <div className="px-5 py-4 space-y-3">{children}</div>
      {footer && (
        <footer className="px-5 py-3 border-t border-line/60">{footer}</footer>
      )}
    </section>
  )
}

function Row({
  label, hint, children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3 items-start">
      <div>
        <label className="text-xs uppercase tracking-wider text-textDim font-medium">
          {label}
        </label>
        {hint && <p className="text-2xs text-textFaint mt-0.5">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  )
}

/* ─── Account card ───────────────────────────────────────────── */

function AccountCard({ data, accountId }: { data: MeSettings; accountId: number | null }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const [label, setLabel] = useState(data.account_label ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dirty = label !== (data.account_label ?? '')

  // Re-sync if server-state changes (after a successful save elsewhere)
  useEffect(() => { setLabel(data.account_label ?? '') }, [data.account_label])

  const save = async () => {
    setSaving(true); setError(null)
    try {
      await patchMeSettings({ account_label: label }, accountId)
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } catch (e) {
      setError(String(e))
    } finally { setSaving(false) }
  }

  return (
    <Card title={t('my_settings.account.title')}
          hint={t('my_settings.account.hint')}
          footer={
            <SaveBar dirty={dirty} saving={saving} error={error}
                     onSave={save} onReset={() => setLabel(data.account_label ?? '')} />
          }>
      <Row label="ID"><code className="text-xs text-textDim">#{data.id}</code></Row>
      {data.email && (
        <Row label={t('my_settings.account.email')}>
          <code className="text-xs">{data.email}</code>
        </Row>
      )}
      <Row label={t('my_settings.account.role')}>
        <div className="flex items-center gap-2">
          <span className={cn('pill text-2xs',
            data.role === 'admin' ? 'bg-fire-faint text-fire' : 'bg-elevated text-textDim',
          )}>{data.role}</span>
          <span className={cn('pill text-2xs',
            data.status === 'active' ? 'bg-pulse-faint text-pulse' : 'bg-amber-faint text-amber',
          )}>{data.status}</span>
          {data.is_master && (
            <span className="pill text-2xs bg-sky-faint text-sky">master</span>
          )}
        </div>
      </Row>
      <Row label={t('my_settings.account.label')}
           hint={t('my_settings.account.label_hint')}>
        <Input value={label} onChange={(e) => setLabel(e.target.value)}
               placeholder={data.email ?? '—'} maxLength={40} />
      </Row>
      <Row label={t('my_settings.account.last_login')}>
        <span className="text-xs text-textFaint">
          {data.last_login_at ? timeAgo(data.last_login_at) : '—'}
        </span>
      </Row>
    </Card>
  )
}

/* ─── API Keys card ──────────────────────────────────────────── */

function ApiKeysCard({ data, accountId }: { data: MeSettings; accountId: number | null }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [k, setK] = useState('')
  const [s, setS] = useState('')
  const [testnet, setTn] = useState(data.bybit_testnet)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    qc.invalidateQueries({ queryKey: ['me', 'bybit-status'] })  // PULSE-464: баннер/бейдж по свежей пробе
  }

  const save = async () => {
    if (!k || !s) { setError('Both fields required'); return }
    setSaving(true); setError(null)
    try {
      await putApiKeys(k, s, testnet, accountId)
      setK(''); setS(''); setEditing(false)
      refresh()
    } catch (e) {
      setError(String(e))
    } finally { setSaving(false) }
  }

  const flip = async (next: boolean) => {
    setSaving(true); setError(null)
    try { await toggleTestnet(next, accountId); refresh() }
    catch (e) { setError(String(e)) }
    finally { setSaving(false) }
  }

  return (
    <Card title={t('my_settings.api.title')}
          hint={t('my_settings.api.hint')}>
      {/* Current state */}
      <Row label={t('my_settings.api.status')}>
        {data.has_api_keys ? (
          <div className="flex items-center gap-2">
            <span className="pill text-2xs bg-pulse-faint text-pulse">
              {t('my_settings.api.configured')}
            </span>
            {data.api_key_masked && (
              <code className="text-xs text-textDim">{data.api_key_masked}</code>
            )}
          </div>
        ) : (
          <span className="pill text-2xs bg-amber-faint text-amber">
            {t('my_settings.api.not_set')}
          </span>
        )}
      </Row>

      {data.has_api_keys && (
        <Row label={t('my_settings.api.mode')}
             hint={t('my_settings.api.mode_hint')}>
          <div className="flex gap-2">
            <Button size="sm"
              variant={data.bybit_testnet ? 'primary' : 'secondary'}
              disabled={saving}
              onClick={() => flip(true)}
            >{t('my_settings.api.demo')}</Button>
            <Button size="sm"
              variant={!data.bybit_testnet ? 'primary' : 'secondary'}
              disabled={saving}
              onClick={() => flip(false)}
            >{t('my_settings.api.real')}</Button>
          </div>
        </Row>
      )}

      {/* Edit form */}
      {editing ? (
        <div className="space-y-3 border-t border-line/40 pt-3">
          <Row label={t('my_settings.api.api_key')}>
            <Input type="password" value={k} onChange={(e) => setK(e.target.value)}
                   placeholder="API key" autoComplete="off" />
          </Row>
          <Row label={t('my_settings.api.api_secret')}>
            <Input type="password" value={s} onChange={(e) => setS(e.target.value)}
                   placeholder="API secret" autoComplete="off" />
          </Row>
          <Row label={t('my_settings.api.testnet')}>
            <Button size="sm"
              variant={testnet ? 'primary' : 'secondary'}
              onClick={() => setTn((v) => !v)}
            >{testnet ? t('my_settings.api.demo') : t('my_settings.api.real')}</Button>
          </Row>
          <p className="text-2xs text-amber">{t('my_settings.api.warning')}</p>
          {error && <p className="text-xs text-rose break-all">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={saving} onClick={save}>
              {saving ? t('my_settings.saving') : t('my_settings.save')}
            </Button>
            <Button size="sm" variant="secondary" disabled={saving}
                    onClick={() => { setEditing(false); setK(''); setS(''); setError(null) }}>
              {t('my_settings.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
          {data.has_api_keys ? t('my_settings.api.replace') : t('my_settings.api.set')}
        </Button>
      )}
    </Card>
  )
}

/* ─── Risk card ──────────────────────────────────────────────── */

function RiskCard({ data, accountId }: { data: MeSettings; accountId: number | null }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const [maxPos, setMax] = useState(data.max_concurrent_positions)
  const [lossPct, setLoss] = useState(data.daily_loss_limit_pct)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dirty = maxPos !== data.max_concurrent_positions
              || lossPct !== data.daily_loss_limit_pct

  useEffect(() => {
    setMax(data.max_concurrent_positions)
    setLoss(data.daily_loss_limit_pct)
  }, [data.max_concurrent_positions, data.daily_loss_limit_pct])

  const save = async () => {
    setSaving(true); setError(null)
    try {
      await patchMeSettings({
        max_concurrent_positions: maxPos,
        daily_loss_limit_pct: lossPct,
      }, accountId)
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } catch (e) { setError(String(e)) }
    finally { setSaving(false) }
  }

  return (
    <Card title={t('my_settings.risk.title')}
          hint={t('my_settings.risk.hint')}
          footer={
            <SaveBar dirty={dirty} saving={saving} error={error}
                     onSave={save}
                     onReset={() => {
                       setMax(data.max_concurrent_positions)
                       setLoss(data.daily_loss_limit_pct)
                     }} />
          }>
      <Row label={t('my_settings.risk.max_concurrent')}
           hint={t('my_settings.risk.max_concurrent_hint')}>
        <Input type="number" min={0} value={maxPos}
               onChange={(e) => setMax(Number(e.target.value))}
               className="font-mono" />
      </Row>
      <Row label={t('my_settings.risk.daily_loss_pct')}
           hint={t('my_settings.risk.daily_loss_pct_hint')}>
        <Input type="number" step={0.1} min={0} value={lossPct}
               onChange={(e) => setLoss(Number(e.target.value))}
               className="font-mono" />
      </Row>
    </Card>
  )
}

/* ─── Telegram card ──────────────────────────────────────────── */


function TelegramCard({ data, accountId }: { data: MeSettings; accountId: number | null }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const [draft, setDraft] = useState({
    telegram_chat_id: data.telegram_chat_id ?? '',
    telegram_enabled: data.telegram_enabled,
    notify_on_open:   data.notify_on_open,
    notify_on_close:  data.notify_on_close,
    notify_on_error:  data.notify_on_error,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null)
  const dirty =
    draft.telegram_chat_id !== (data.telegram_chat_id ?? '') ||
    draft.telegram_enabled !== data.telegram_enabled ||
    draft.notify_on_open   !== data.notify_on_open ||
    draft.notify_on_close  !== data.notify_on_close ||
    draft.notify_on_error  !== data.notify_on_error

  useEffect(() => {
    setDraft({
      telegram_chat_id: data.telegram_chat_id ?? '',
      telegram_enabled: data.telegram_enabled,
      notify_on_open:   data.notify_on_open,
      notify_on_close:  data.notify_on_close,
      notify_on_error:  data.notify_on_error,
    })
  }, [data])

  const save = async () => {
    setSaving(true); setError(null)
    try {
      await patchMeSettings({
        telegram_chat_id: draft.telegram_chat_id || null,
        telegram_enabled: draft.telegram_enabled,
        notify_on_open:   draft.notify_on_open,
        notify_on_close:  draft.notify_on_close,
        notify_on_error:  draft.notify_on_error,
      }, accountId)
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } catch (e) { setError(String(e)) }
    finally { setSaving(false) }
  }

  const test = async () => {
    setTestResult(null)
    try {
      const r = await testTelegram(accountId)
      setTestResult({
        ok: r.success,
        msg: r.success ? (r.message ?? 'ok') : (r.error ?? 'failed'),
      })
    } catch (e) {
      setTestResult({ ok: false, msg: String(e) })
    }
  }

  return (
    <Card title={t('my_settings.tg.title')}
          hint={t('my_settings.tg.hint')}
          footer={
            <div className="flex items-center gap-3 w-full">
              {testResult && (
                <span className={cn('text-xs', testResult.ok ? 'text-pulse' : 'text-rose')}>
                  {testResult.msg}
                </span>
              )}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="secondary"
                        disabled={!data.telegram_chat_id} onClick={test}>
                  {t('my_settings.tg.send_test')}
                </Button>
                <SaveBar inline dirty={dirty} saving={saving} error={error}
                         onSave={save}
                         onReset={() => setDraft({
                           telegram_chat_id: data.telegram_chat_id ?? '',
                           telegram_enabled: data.telegram_enabled,
                           notify_on_open: data.notify_on_open,
                           notify_on_close: data.notify_on_close,
                           notify_on_error: data.notify_on_error,
                         })} />
              </div>
            </div>
          }>
      <Row label={t('my_settings.tg.chat_id')}
           hint={t('my_settings.tg.chat_id_hint')}>
        <Input value={draft.telegram_chat_id}
               onChange={(e) => setDraft({ ...draft, telegram_chat_id: e.target.value })}
               placeholder="123456789" className="font-mono" />
      </Row>
      <Row label={t('my_settings.tg.enabled')}>
        <Button size="sm"
          variant={draft.telegram_enabled ? 'primary' : 'secondary'}
          onClick={() => setDraft({ ...draft, telegram_enabled: !draft.telegram_enabled })}
          className="w-full"
        >{draft.telegram_enabled ? 'ON' : 'OFF'}</Button>
      </Row>
      {draft.telegram_enabled && (
        <div className="space-y-2 border-t border-line/40 pt-3">
          <p className="text-2xs text-textFaint uppercase tracking-wider">
            {t('my_settings.tg.notify_on')}
          </p>
          <NotifyToggle
            label={t('my_settings.tg.on_open')}
            value={draft.notify_on_open}
            onChange={(v) => setDraft({ ...draft, notify_on_open: v })}
          />
          <NotifyToggle
            label={t('my_settings.tg.on_close')}
            value={draft.notify_on_close}
            onChange={(v) => setDraft({ ...draft, notify_on_close: v })}
          />
          <NotifyToggle
            label={t('my_settings.tg.on_error')}
            value={draft.notify_on_error}
            onChange={(v) => setDraft({ ...draft, notify_on_error: v })}
          />
        </div>
      )}
    </Card>
  )
}

function NotifyToggle({
  label, value, onChange,
}: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <input type="checkbox" className="accent-fire"
             checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-textDim">{label}</span>
    </label>
  )
}

/* ─── Sub-accounts card ──────────────────────────────────────── */

function SubAccountsCard({ data }: { data: MeSettings }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const [label, setLabel] = useState('')
  const [demo, setDemo] = useState(true)
  const [exp, setExp] = useState(false)
  const [creating, setCreating] = useState(false)

  const create = async () => {
    if (!label.trim() || creating) return
    setCreating(true)
    try {
      await createSubAccount({ account_label: label.trim(), bybit_testnet: demo, exclude_from_apply: exp })
      setLabel('')
      setDemo(true)
      setExp(false)
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } finally { setCreating(false) }
  }

  return (
    <Card title={t('my_settings.subs.title')} hint={t('my_settings.subs.hint')}>
      <table className="w-full text-sm">
        <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line">
          <tr>
            <th className="text-left py-2">{t('my_settings.subs.col_id')}</th>
            <th className="text-left py-2">{t('my_settings.subs.col_label')}</th>
            <th className="text-left py-2">{t('my_settings.subs.col_mode')}</th>
            <th className="text-left py-2">{t('my_settings.subs.col_keys')}</th>
            <th className="text-left py-2">{t('my_settings.subs.col_active')}</th>
            <th className="text-left py-2">{t('my_settings.subs.col_exp')}</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {data.sub_accounts.map((s) => (
            <SubAccountRow key={s.id} sub={s} />
          ))}
        </tbody>
      </table>

      {/* Create new sub-account */}
      <div className="mt-4 pt-4 border-t border-line flex flex-wrap items-center gap-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('my_settings.subs.add_placeholder')}
          maxLength={40}
          className="font-mono text-xs flex-1 min-w-[160px]"
          disabled={creating}
        />
        <div className="flex gap-1">
          <Button size="sm" variant={demo ? 'primary' : 'secondary'}
                  onClick={() => setDemo(true)} disabled={creating}>
            {t('my_settings.api.demo')}
          </Button>
          <Button size="sm" variant={!demo ? 'primary' : 'secondary'}
                  onClick={() => setDemo(false)} disabled={creating}>
            {t('my_settings.api.real')}
          </Button>
        </div>
        <Button size="sm" variant={exp ? 'primary' : 'secondary'}
                onClick={() => setExp(!exp)} disabled={creating}
                title={t('my_settings.subs.exp_hint')}>
          {t('my_settings.subs.col_exp')}{exp ? ' ✓' : ''}
        </Button>
        <Button size="sm" variant="primary"
                onClick={create} disabled={creating || !label.trim()}>
          {t('my_settings.subs.add_btn')}
        </Button>
      </div>
    </Card>
  )
}

function SubAccountRow({ sub }: { sub: MeSubAccount }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const [label, setLabel] = useState(sub.account_label ?? '')
  const [saving, setSaving] = useState(false)
  const dirty = label !== (sub.account_label ?? '')

  useEffect(() => setLabel(sub.account_label ?? ''), [sub.account_label])

  const saveLabel = async () => {
    if (!dirty) return
    setSaving(true)
    try {
      await patchSubAccount(sub.id, { account_label: label })
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } finally { setSaving(false) }
  }
  const toggleActive = async () => {
    setSaving(true)
    try {
      await patchSubAccount(sub.id, { is_active: !sub.is_active })
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } finally { setSaving(false) }
  }
  const toggleExperimental = async () => {
    setSaving(true)
    try {
      await patchSubAccount(sub.id, { exclude_from_apply: !sub.exclude_from_apply })
      qc.invalidateQueries({ queryKey: ['me', 'settings'] })
    } finally { setSaving(false) }
  }

  return (
    <tr className="border-b border-line/40">
      <td className="py-2 text-textFaint">#{sub.id}</td>
      <td className="py-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)}
               onBlur={saveLabel} className="font-mono text-xs"
               placeholder="(no label)" disabled={saving} />
      </td>
      <td className="py-2">
        <span className={cn('pill text-2xs',
          sub.bybit_testnet ? 'bg-amber-faint text-amber' : 'bg-pulse-faint text-pulse',
        )}>{sub.bybit_testnet ? t('my_settings.api.demo') : t('my_settings.api.real')}</span>
      </td>
      <td className="py-2 text-xs">
        {sub.has_api_keys ? '✓' : '—'}
      </td>
      <td className="py-2">
        <Button size="sm"
          variant={sub.is_active ? 'primary' : 'secondary'}
          disabled={saving}
          onClick={toggleActive}
        >{sub.is_active ? 'ON' : 'OFF'}</Button>
      </td>
      <td className="py-2">
        <Button size="sm"
          variant={sub.exclude_from_apply ? 'primary' : 'secondary'}
          disabled={saving}
          onClick={toggleExperimental}
          title={t('my_settings.subs.exp_hint')}
        >{sub.exclude_from_apply ? 'EXP' : '—'}</Button>
      </td>
    </tr>
  )
}

/* ─── Risk blocks card ────────────────────────────────────────── */

function RiskBlocksCard({ isMaster }: { isMaster: boolean }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { data: blocks, isLoading } = useMyRiskBlocks()
  const [clearing, setClearing] = useState<number | null>(null)

  const clear = async (id: number) => {
    setClearing(id)
    try {
      await clearMyRiskBlock(id)
      qc.invalidateQueries({ queryKey: ['me', 'risk-blocks'] })
    } finally { setClearing(null) }
  }

  return (
    <Card title={t('my_settings.blocks.title')} hint={t('my_settings.blocks.hint')}>
      {isLoading ? (
        <p className="text-textFaint text-sm">—</p>
      ) : !blocks || blocks.length === 0 ? (
        <p className="text-textFaint text-sm">{t('my_settings.blocks.empty')}</p>
      ) : (
        <ul className="divide-y divide-line/40 font-mono text-xs">
          {blocks.map((b) => (
            <BlockRow key={b.id} block={b} isMaster={isMaster}
                      clearing={clearing === b.id}
                      onClear={() => clear(b.id)} />
          ))}
        </ul>
      )}
    </Card>
  )
}

function BlockRow({
  block, isMaster, clearing, onClear,
}: {
  block: UserRiskBlock
  isMaster: boolean
  clearing: boolean
  onClear: () => void
}) {
  const { t } = useTranslation('app')
  return (
    <li className="py-2 flex items-center gap-3">
      <span className="pill text-2xs bg-rose-faint text-rose">
        {block.block_type}
      </span>
      <span className="text-textDim">user #{block.user_id}</span>
      {block.strategy_name && (
        <span className="text-text">{block.strategy_name}</span>
      )}
      <span className="text-text flex-1 truncate" title={block.reason}>
        {block.reason}
      </span>
      <span className="text-textFaint">
        → {new Date(block.blocked_until).toLocaleString()}
      </span>
      {isMaster && (
        <Button size="sm" variant="secondary" disabled={clearing} onClick={onClear}>
          {clearing ? '…' : t('my_settings.blocks.clear')}
        </Button>
      )}
    </li>
  )
}

/* ─── Save bar primitive ──────────────────────────────────────── */

function SaveBar({
  dirty, saving, error, onSave, onReset, inline = false,
}: {
  dirty: boolean
  saving: boolean
  error: string | null
  onSave: () => void
  onReset: () => void
  inline?: boolean
}) {
  const { t } = useTranslation('app')
  return (
    <div className={cn('flex items-center gap-3', inline ? '' : 'w-full')}>
      {error && <span className="text-xs text-rose break-all">{error}</span>}
      {dirty && !error && (
        <span className="text-xs text-amber">{t('my_settings.unsaved')}</span>
      )}
      <div className="ml-auto flex gap-2">
        <Button size="sm" variant="secondary"
                disabled={!dirty || saving} onClick={onReset}>
          {t('my_settings.reset')}
        </Button>
        <Button size="sm" variant="primary"
                disabled={!dirty || saving} onClick={onSave}>
          {saving ? t('my_settings.saving') : t('my_settings.save')}
        </Button>
      </div>
    </div>
  )
}
